import React, {useLayoutEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogActions} from '@mui/material';
import {LocalizationProvider, StaticDatePicker, StaticTimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  DEFAULT_PICKER_HOUR,
  formatApptClock,
  formatApptDate,
  formatApptDateTime,
  hasTimeOfDay,
  withDate,
  withTime,
} from './appointment-time';

/*
 * Appointment Info, Android's AppointmentSectionView
 * (estimatebuilder/sections/appointment): a card with its own caret. View
 * mode reads out the date and time and the notes; edit mode has the date and
 * time buttons (2 : 1, at every width) and the notes, which the page
 * autosaves as they change. The pickers are Android's dialogs: a calendar
 * and a clock, small and centred, each with Cancel and OK.
 */

// ---- The pickers --------------------------------------------------------------------------

/** No action bar of the picker's own: the dialog's Cancel and OK are the only way out. */
const NO_ACTION_BAR = {actionBar: () => null};

const DatePickerDialog = ({value, onPicked, onClose}: {value?: Date; onPicked: (date: Date) => void; onClose: () => void}) => {
  // The day the calendar opens on; nothing is set until OK.
  const [day, setDay] = useState<Dayjs>(dayjs(value ?? new Date()));
  return (
    <Dialog open onClose={onClose} PaperProps={{'aria-label': 'Select date'}}>
      <StaticDatePicker displayStaticWrapperAs="mobile" value={day} onChange={next => next && setDay(next)}
                        slots={NO_ACTION_BAR}/>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onPicked(withDate(value, {year: day.year(), month: day.month(), date: day.date()}))}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

const TimePickerDialog = ({value, onPicked, onClose}: {value?: Date; onPicked: (date: Date) => void; onClose: () => void}) => {
  const set = value && hasTimeOfDay(value) ? value : undefined;
  // Where the dial starts: the time already set, else 9:00 AM.
  const [clock, setClock] = useState<Dayjs>(dayjs().hour(set?.getHours() ?? DEFAULT_PICKER_HOUR).minute(set?.getMinutes() ?? 0));
  return (
    <Dialog open onClose={onClose} PaperProps={{'aria-label': 'Select time'}}>
      <StaticTimePicker displayStaticWrapperAs="mobile" ampm value={clock} onChange={next => next && setClock(next)}
                        slots={NO_ACTION_BAR}/>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onPicked(withTime(value, clock.hour(), clock.minute()))}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- The notes field ------------------------------------------------------------------------

/** Android's NoteTextField: one line to start with, growing as it is typed into. */
const NotesField = ({value, onChange}: {value: string; onChange: (value: string) => void}) => {
  const box = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea ref={box} className="ap-notes" rows={1} value={value} placeholder="Appointment Notes" autoCapitalize="sentences"
              aria-label="Appointment Notes" onChange={e => onChange(e.target.value)}/>
  );
};

// ---- The card -------------------------------------------------------------------------------

export interface AppointmentInfoCardProps {
  time?: Date;
  notes: string;
  editing: boolean;
  open: boolean;
  onToggle: () => void;
  onTimeChanged: (time: Date) => void;
  onNotesChanged: (notes: string) => void;
}

/**
 * The saved card, opened: the date and time, then the notes. A missing date
 * says so; missing notes say nothing, since most appointments have none.
 * With neither, one line says so.
 */
const Summary = ({time, notes}: {time?: Date; notes: string}) => {
  const hasNotes = notes.trim() !== '';
  if (!time && !hasNotes) return <div className="ap-info-line ap-info-line--muted">No date, time or notes</div>;
  return (
    <>
      <div className={time ? 'ap-info-line' : 'ap-info-line ap-info-line--muted'}>
        {time ? formatApptDateTime(time) : 'No date/time set'}
      </div>
      {hasNotes && <div className="ap-info-notes">{notes}</div>}
    </>
  );
};

export const AppointmentInfoCard = ({time, notes, editing, open, onToggle, onTimeChanged, onNotesChanged}: AppointmentInfoCardProps) => {
  const [picker, setPicker] = useState<'date' | 'time' | null>(null);
  const picked = (next: Date) => {
    setPicker(null);
    onTimeChanged(next);
  };
  return (
    <div className="ap-info">
      <button type="button" className="ap-info-head" onClick={onToggle} aria-expanded={open}>
        <span>Appointment Info</span>
        {open ? <KeyboardArrowUpIcon aria-label="Collapse"/> : <KeyboardArrowDownIcon aria-label="Expand"/>}
      </button>
      {open && !editing && <Summary time={time} notes={notes}/>}
      {open && editing && <>
        {/* Two selectors, one value: each edits its own half of the moment. */}
        <div className="ap-when">
          <button type="button" className="ap-when-button ap-when-button--date" onClick={() => setPicker('date')}>
            {time ? formatApptDate(time) : 'Set date'}
          </button>
          <button type="button" className="ap-when-button ap-when-button--time" onClick={() => setPicker('time')}>
            {time && hasTimeOfDay(time) ? formatApptClock(time) : 'Set time'}
          </button>
        </div>
        <NotesField value={notes} onChange={onNotesChanged}/>
      </>}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {picker === 'date' && <DatePickerDialog value={time} onPicked={picked} onClose={() => setPicker(null)}/>}
        {picker === 'time' && <TimePickerDialog value={time} onPicked={picked} onClose={() => setPicker(null)}/>}
      </LocalizationProvider>
    </div>
  );
};
